import type {
    IPagination,
    IUserDecoratedLicense,
} from '@experiences/interfaces';
import type { LanguageCulture } from '@experiences/locales';
import keyBy from 'lodash/keyBy';
import merge from 'lodash/merge';
import values from 'lodash/values';

import type {
    IBasicUserDto,
    IBulkInviteUserDto,
    IBulkInviteUserResponse,
    IUpdateUserDto,
    IUserCIS,
} from '../../common/interfaces/cis/user';
import type { IResult } from '../../common/interfaces/result';
import type { IUserCISResponse } from '../../component/users/interfaces/userLicense';
import { updateUserGroups } from '../../store/action/UserProfileAction';
import {
    deallocateCurrentUserLicense,
    deleteUserExplicitAllocations,
    removeUserFromGroups,
    removeUsersFromLicenseGroup,
} from '../licensing/accountant/UserLicenseService';
import { getUserLicenseUsers } from '../licensing/UserLicenseService';
import { cleanUserCISResponse } from '../mappers/UserPartitionServiceMapper';
import {
    axiosDelete,
    get,
    post,
    put,
} from '../utility/Requests';
import {
    getIdentityBaseRoute,
    validateLanguageCode,
} from './IdentityServiceUtils';

export const userUrl = `/api/identity/User`;

export function getUser({
    url, userGlobalId, partitionGlobalId, isHostMode,
}: { url?: string; userGlobalId: string; partitionGlobalId: string; isHostMode: boolean }) {
    return get<IUserCIS>(`${getIdentityBaseRoute(partitionGlobalId, isHostMode)}/api/User/${userGlobalId}`, { ignoreBasePath: true });
}

export async function createUser(basicUserDto: IBasicUserDto, isHostMode: boolean = false) {
    return await post<IResult>(
        `${getIdentityBaseRoute(basicUserDto.partitionGlobalId, isHostMode)}/api/User`,
        {
            body: basicUserDto,
            ignoreBasePath: true,
        },
    );
}

export async function putUser(
    userGlobalId: string,
    userData: IUpdateUserDto,
    isCurrentUser: boolean,
    partitionGlobalId: string,
    enableLegacyRemoveUserFromGroups: boolean,
    isOrgAdmin: boolean = true,
    isHostMode: boolean = false,
) {
    await put<any>(
        `${getIdentityBaseRoute(partitionGlobalId, isHostMode)}/api/User/${userGlobalId}`,
        {
            body: userData,
            ignoreBasePath: true,
        },
    );

    if (isOrgAdmin && userData.groupIDsToRemove?.length) {
        if (enableLegacyRemoveUserFromGroups) {
            await Promise.all(
                userData.groupIDsToRemove.map(groupId =>
                    removeUsersFromLicenseGroup(groupId, [ userGlobalId ]),
                ),
            );
        } else {
            await removeUserFromGroups(userGlobalId, userData.groupIDsToRemove);
        }
    }

    if (isCurrentUser && userData.groupIDsToAdd) {
        await updateUserGroups('update', userData.groupIDsToAdd);
    }
}

export async function deleteUsers(partitionGlobalId: string, userIds: string[], deleteCurrentUser: boolean = false, isHostMode: boolean = false, isOrgAdmin: boolean = true) {
    if (!isHostMode && isOrgAdmin) {
        if (deleteCurrentUser) {
            await deallocateCurrentUserLicense();
        } else {
            await deleteUserExplicitAllocations(userIds);
        }
    }

    return await axiosDelete(
        `${getIdentityBaseRoute(partitionGlobalId, isHostMode)}/api/User`,
        {
            body: {
                partitionGlobalId,
                userIds,
            },
            ignoreBasePath: true,
        },
    );
}

export async function inviteUsers(
    inviteData: IBulkInviteUserDto[],
    provider?: string,
    language?: LanguageCulture,
    orgName?: string,
    partitionGlobalId?: string,
) {
    const nonLocalEnvs = [ 'alp', 'agov', 'sgov', 'pgov', 'nst', 'prd', 'stg', 'portal-only', 'portal-only-gov' ];
    const originUrl = nonLocalEnvs.includes(window.env?.ENVIRONMENT?.toLowerCase() ?? '')
        ? window.env?.PORTAL_ORIGIN
        : window.location.origin;
    const orgNameParam = orgName ? `&organizationName=${orgName}` : '';
    const validatedLanguage = validateLanguageCode(language ?? 'en');

    const enrichedInviteData = inviteData.map(data => ({
        ...data,
        redirectUrl:
            `${originUrl}/portal_/acceptInvite?organizationId=${partitionGlobalId}&emailForUserinvite=${encodeURIComponent(data.email ?? '')}${orgNameParam}` +
            (provider ? `&provider=${provider.toLowerCase()}` : ''),
        language: validatedLanguage,
    }));

    return await post<IBulkInviteUserResponse>(
        `${getIdentityBaseRoute(partitionGlobalId!, false)}/api/User/InviteUsers`,
        {
            body: enrichedInviteData,
            ignoreBasePath: true,
        },
    );
}

export async function getUsersInOrg({
    pagination, partitionGlobalId, isHostMode,
}: {
    pagination: IPagination;
    partitionGlobalId: string;
    isHostMode: boolean;
}): Promise<IUserCISResponse> {
    const response = await get<IUserCISResponse>(`${getIdentityBaseRoute(partitionGlobalId, isHostMode)}/api/User/users/${partitionGlobalId}`, {
        pagination,
        ignoreBasePath: true,
    });
    return cleanUserCISResponse(response);
}

export async function getUsersInOrgWithLicenses({
    pagination, partitionGlobalId, isHostMode,
}: {
    pagination: IPagination;
    partitionGlobalId: string;
    isHostMode: boolean;
}): Promise<IUserCISResponse> {
    const usersResponse = await get<IUserCISResponse>(
        `${getIdentityBaseRoute(partitionGlobalId, isHostMode)}/api/User/users/${partitionGlobalId}`,
        {
            pagination,
            ignoreBasePath: true,
        },
    );

    const userIds = usersResponse.results.map((user: IUserCIS) => user.id);
    const licenseData = await getUserLicenseUsers({ userIds }) as unknown as IUserDecoratedLicense[];

    const resultsWithLicenses = merge(keyBy(usersResponse.results, 'id'), keyBy(licenseData, 'userId'));
    const results = values(resultsWithLicenses);

    return cleanUserCISResponse({
        totalCount: usersResponse.totalCount,
        results,
    } as IUserCISResponse);
}
